/**
 * OWL2 Ontology Container
 *
 * Defines the root Ontology structure that contains all axioms,
 * declarations, prefixes, imports, and annotations.
 */

import type { IRI } from './IRI.ts';
import type { Axiom } from './Axiom.ts';
import type { Annotation } from './Annotation.ts';

// =============================================================================
// Prefix Declaration
// =============================================================================

/**
 * Prefix declaration mapping a prefix name to a namespace IRI.
 * Example: Prefix(ex: = <http://example.org/>)
 */
export interface PrefixDeclaration {
    /** The prefix name (without the colon) */
    readonly name: string;
    /** The namespace IRI */
    readonly iri: string;
}

// =============================================================================
// Import Declaration
// =============================================================================

/**
 * Import declaration referencing another ontology.
 * OWL2: Import(IRI)
 */
export interface Import {
    readonly iri: IRI;
}

// =============================================================================
// Ontology
// =============================================================================

/**
 * OWL2 Ontology - the root container for all ontology content.
 *
 * An ontology contains:
 * - Optional ontology IRI (identifier)
 * - Optional version IRI
 * - Prefix declarations for abbreviated IRIs
 * - Import declarations for referenced ontologies
 * - Annotations on the ontology itself
 * - Axioms (declarations, class axioms, property axioms, assertions, etc.)
 */
export interface Ontology {
    /** Optional ontology IRI - the identifier for this ontology */
    readonly ontologyIRI?: IRI;

    /** Optional version IRI - identifies a specific version */
    readonly versionIRI?: IRI;

    /** Prefix declarations for namespace abbreviation */
    readonly prefixes: readonly PrefixDeclaration[];

    /** Import declarations for referenced ontologies */
    readonly imports: readonly Import[];

    /** Annotations on the ontology itself */
    readonly annotations: readonly Annotation[];

    /** All axioms in the ontology */
    readonly axioms: readonly Axiom[];
}

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create a prefix declaration.
 */
export function prefixDeclaration(name: string, iri: string): PrefixDeclaration {
    return { name, iri };
}

/**
 * Create an import declaration.
 */
export function importDeclaration(iri: IRI): Import {
    return { iri };
}

/**
 * Create an empty ontology.
 */
export function emptyOntology(): Ontology {
    return {
        prefixes: [],
        imports: [],
        annotations: [],
        axioms: []
    };
}

/**
 * Create an ontology with the given IRI.
 */
export function ontology(
    ontologyIRI?: IRI,
    options?: {
        versionIRI?: IRI;
        prefixes?: readonly PrefixDeclaration[];
        imports?: readonly Import[];
        annotations?: readonly Annotation[];
        axioms?: readonly Axiom[];
    }
): Ontology {
    return {
        ...(ontologyIRI && { ontologyIRI }),
        ...(options?.versionIRI && { versionIRI: options.versionIRI }),
        prefixes: options?.prefixes ?? [],
        imports: options?.imports ?? [],
        annotations: options?.annotations ?? [],
        axioms: options?.axioms ?? []
    };
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Add a prefix declaration to an ontology (returns new ontology).
 */
export function addPrefix(onto: Ontology, prefix: PrefixDeclaration): Ontology {
    return {
        ...onto,
        prefixes: [...onto.prefixes, prefix]
    };
}

/**
 * Add an import declaration to an ontology (returns new ontology).
 */
export function addImport(onto: Ontology, imp: Import): Ontology {
    return {
        ...onto,
        imports: [...onto.imports, imp]
    };
}

/**
 * Add an annotation to an ontology (returns new ontology).
 */
export function addOntologyAnnotation(onto: Ontology, ann: Annotation): Ontology {
    return {
        ...onto,
        annotations: [...onto.annotations, ann]
    };
}

/**
 * Add an axiom to an ontology (returns new ontology).
 */
export function addAxiom(onto: Ontology, axiom: Axiom): Ontology {
    return {
        ...onto,
        axioms: [...onto.axioms, axiom]
    };
}

/**
 * Add multiple axioms to an ontology (returns new ontology).
 */
export function addAxioms(onto: Ontology, axioms: readonly Axiom[]): Ontology {
    return {
        ...onto,
        axioms: [...onto.axioms, ...axioms]
    };
}

/**
 * Get all axioms of a specific type from an ontology.
 */
export function getAxiomsOfType<T extends Axiom>(
    onto: Ontology,
    predicate: (axiom: Axiom) => axiom is T
): readonly T[] {
    return onto.axioms.filter(predicate);
}

/**
 * Find a prefix by name.
 */
export function findPrefix(onto: Ontology, name: string): PrefixDeclaration | undefined {
    return onto.prefixes.find(p => p.name === name);
}

/**
 * Expand an abbreviated IRI using the ontology's prefix declarations.
 */
export function expandIRI(onto: Ontology, abbreviated: string): string | undefined {
    const colonIndex = abbreviated.indexOf(':');
    if (colonIndex === -1) return undefined;

    const prefixName = abbreviated.substring(0, colonIndex);
    const localName = abbreviated.substring(colonIndex + 1);

    const prefix = findPrefix(onto, prefixName);
    if (!prefix) return undefined;

    return prefix.iri + localName;
}
